import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useThemeMode } from "@/lib/useThemeMode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import api from "@/lib/api";
import {
  Brain, ArrowUpRight, Check, Sun, Moon,
  FileText, Image as ImageIcon, MessageCircle, Pill, ScanLine,
  Layers, Sparkles, Watch, Waves, ShieldCheck, Share2,
  GitMerge, LineChart, Upload, Twitter, Instagram, Linkedin,
} from "lucide-react";

/**
 * Memora — Landing.
 *
 * Design grounded in what the mobile app actually looks like:
 *  • Brand mark = Brain in a peach halo (identical to splash screen)
 *  • Palette / dark & light modes = the mobile theme tokens verbatim
 *  • Icon "constellation" = the onboarding CaptureVisual pattern
 *  • Product preview = a static echo of Memora + Trends
 *  • Typography = Jost 400/500/600/700, JetBrains Mono for numerics
 *  • Zero italics anywhere, per the brief
 */

// ---- tokens mirrored from /app/mobile/src/lib/theme.ts ------------------
const PALETTE = {
  peach: "#F6CFBE",
  blue: "#B9DCF2",
  lavender: "#D9D0E9",
  metabolism: "#4CC9F0",
  heart: "#FF6B6B",
  resilience: "#FFD93D",
  mental: "#9D4EDD",
  mint: "#B4E5C8",
};

const CAPABILITIES = [
  { Icon: Layers,      color: PALETTE.blue,       title: "It gathers.",     body: "Labs, prescriptions, discharge notes, imaging, wearables, portal messages. Snap a photo, upload a file, or connect a source — Memora files it all in the same place." },
  { Icon: GitMerge,    color: PALETTE.peach,      title: "It harmonises.",  body: "Two hospitals record the same test in three different units and two different names. Memora reconciles them into one consistent history — every source kept, every contradiction visible." },
  { Icon: Brain,       color: PALETTE.lavender,   title: "It remembers.",   body: "Every reading, every dose, every visit — dated and connected across the years. Nothing is dropped between clinicians, nothing is lost between moves." },
  { Icon: LineChart,   color: PALETTE.metabolism, title: "It gives you insights.", body: "See patterns across years — how your blood pressure moved with your weight, when your sleep debt caught up with you, what changed after your last medication switch. All drawn only from your own data." },
  { Icon: ShieldCheck, color: PALETTE.mental,     title: "It stays yours.", body: "Patient-owned by design. Export the full record in one file. Share with a doctor or family member through a link you create, expire, and revoke." },
];

const STORY = [
  {
    n: "01",
    h: "Bring it in.",
    p: "Snap a lab, upload a PDF, forward a portal message, or connect a wearable. Multi-page uploads auto-group into one visit — no folders, no tagging.",
    Icon: Upload,
    color: PALETTE.blue,
  },
  {
    n: "02",
    h: "Memora files it.",
    p: "Values, medications, findings — extracted, dated, and connected. When reports disagree, both stay on record with provenance intact.",
    Icon: GitMerge,
    color: PALETTE.peach,
  },
  {
    n: "03",
    h: "Ask in your own words.",
    p: "\"Am I trending toward hypertension again?\" Memora finds the answer inside your own history and shows the exact record it came from.",
    Icon: MessageCircle,
    color: PALETTE.mental,
  },
];

const FAQ = [
  { q: "Is Memora a doctor?", a: "No. Memora doesn't diagnose, prescribe, or replace medical advice. It simply holds your history so you and your doctor start the conversation with the same information at the same time." },
  { q: "Where does the data come from?", a: "From you. Snap a photo of a lab report, upload a PDF, forward a message from your patient portal, or connect a wearable. Nothing enters Memora that you didn't put there." },
  { q: "Who else can see my records?", a: "Only you — until you decide otherwise. Every share is a link you create with an expiry you choose. You can revoke access at any moment, and we log every time the link is opened." },
  { q: "How is this different from my patient portal?", a: "A portal shows you one clinic's slice of you. Memora holds every clinic, every year, and every device in one place — and rewrites the clinical language into your language." },
  { q: "What if I want to leave?", a: "Export the entire record as a single file in one click, then delete your account. Your data was always yours; leaving is meant to feel that way." },
  { q: "How do I become a tester?", a: "Fill in the form below. We're inviting people one at a time so we can listen closely and iterate. If we can't take you this week, we'll hold your handle for the next round." },
];

// ---- Brand mark: Brain in peach halo (from mobile splash) ---------------
function BrainMark({ size = 40 }) {
  return (
    <div
      className="relative flex items-center justify-center rounded-full"
      style={{
        width: size, height: size,
        backgroundColor: "rgba(246,207,190,0.14)",
        border: "1px solid rgba(246,207,190,0.32)",
      }}
      aria-hidden
    >
      <Brain size={size * 0.5} color={PALETTE.peach} strokeWidth={1.8} />
    </div>
  );
}

// ---- Hero constellation (based on onboarding CaptureVisual) --------------
function HeroConstellation() {
  const chips = [
    { Icon: FileText,       label: "PDF",       color: PALETTE.blue,       tilt: -14 },
    { Icon: ImageIcon,      label: "Scan",      color: PALETTE.peach,      tilt:  -5 },
    { Icon: MessageCircle,  label: "Portal",    color: PALETTE.lavender,   tilt:   5 },
    { Icon: Pill,           label: "Rx",        color: PALETTE.mint,       tilt:  14 },
  ];
  const orbiters = [
    { Icon: Watch,    color: PALETTE.metabolism, r: 190, angle: -40, label: "Watch" },
    { Icon: Waves,    color: PALETTE.heart,      r: 195, angle:  30, label: "Signal" },
    { Icon: ScanLine, color: PALETTE.mental,     r: 200, angle: 165, label: "Scan"  },
    { Icon: Share2,   color: PALETTE.resilience, r: 200, angle: 220, label: "Share" },
  ];

  return (
    <div className="relative w-full h-[420px] md:h-[520px] flex items-center justify-center select-none">
      {/* Halo rings */}
      <div className="absolute w-[320px] h-[320px] md:w-[420px] md:h-[420px] rounded-full border border-neutral-200 dark:border-white/10" />
      <div className="absolute w-[440px] h-[440px] md:w-[560px] md:h-[560px] rounded-full border border-neutral-200/60 dark:border-white/[0.06]" />
      <div
        className="absolute w-[300px] h-[300px] md:w-[400px] md:h-[400px] rounded-full blur-3xl opacity-70"
        style={{ background: "radial-gradient(circle at 50% 50%, rgba(246,207,190,0.55) 0%, rgba(185,220,242,0.35) 55%, rgba(185,220,242,0) 75%)" }}
      />

      {/* Center brain */}
      <div
        className="relative z-10 w-[140px] h-[140px] md:w-[168px] md:h-[168px] rounded-full flex items-center justify-center"
        style={{
          backgroundColor: "rgba(246,207,190,0.18)",
          border: "1px solid rgba(246,207,190,0.42)",
          boxShadow: "0 20px 60px -20px rgba(246,207,190,0.4)",
        }}
      >
        <Brain size={72} color={PALETTE.peach} strokeWidth={1.6} />
      </div>

      {/* Center chip fan */}
      <div className="absolute z-20 flex gap-2 md:gap-3 translate-y-[110px] md:translate-y-[140px]">
        {chips.map((c) => (
          <div
            key={c.label}
            className="flex flex-col items-center gap-1 px-3 py-2 rounded-2xl bg-white dark:bg-white/[0.04] border border-neutral-200 dark:border-white/10 shadow-sm"
            style={{ transform: `rotate(${c.tilt}deg) translateY(${Math.abs(c.tilt) * 0.6}px)` }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${c.color}22` }}
            >
              <c.Icon size={16} color={c.color} strokeWidth={1.8} />
            </div>
            <span className="text-[10px] font-medium tracking-wide text-neutral-500 dark:text-white/60 uppercase">
              {c.label}
            </span>
          </div>
        ))}
      </div>

      {/* Orbiters */}
      {orbiters.map((o) => {
        const rad = (o.angle * Math.PI) / 180;
        const x = Math.cos(rad) * (o.r * 0.55);
        const y = Math.sin(rad) * (o.r * 0.55);
        return (
          <div
            key={o.label}
            className="absolute z-10 w-11 h-11 rounded-full flex items-center justify-center bg-white dark:bg-white/[0.04] border border-neutral-200 dark:border-white/10 shadow-sm"
            style={{ transform: `translate(${x}px, ${y}px)` }}
          >
            <o.Icon size={18} color={o.color} strokeWidth={1.8} />
          </div>
        );
      })}
    </div>
  );
}

// ---- Product preview: static Memora chat + inline chart -----------------
function ChatPreview() {
  const points = [122, 128, 118, 116, 121, 114, 112]; // fake systolic BP
  const max = Math.max(...points);
  const min = Math.min(...points);
  return (
    <div className="rounded-3xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/[0.03] backdrop-blur-sm p-6 md:p-8 max-w-[560px] w-full shadow-sm">
      <div className="flex items-center gap-2 pb-4 mb-4 border-b border-neutral-200 dark:border-white/10">
        <BrainMark size={26} />
        <div className="text-[13px] font-medium">Memora</div>
        <div className="ml-auto text-[11px] font-mono tracking-tight text-neutral-500 dark:text-white/40">
          / preview
        </div>
      </div>

      {/* User bubble */}
      <div className="flex justify-end mb-4">
        <div className="bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[80%]">
          <p className="text-[14px]">How has my blood pressure been?</p>
        </div>
      </div>

      {/* Assistant text */}
      <p className="text-[14.5px] leading-[1.6] text-neutral-800 dark:text-white/85">
        Trending down over the last seven readings. You&apos;re averaging{" "}
        <span className="font-mono font-medium">118 mmHg</span> — inside the calm range, and{" "}
        <span className="font-mono font-medium">−6</span> from your March baseline.
      </p>

      {/* Inline chart block */}
      <div className="mt-5 rounded-2xl border border-neutral-200 dark:border-white/10 p-4 bg-neutral-50 dark:bg-white/[0.02]">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[13px] font-medium">Systolic BP</div>
          <div className="text-[11px] font-mono tracking-tight text-neutral-500 dark:text-white/40">
            mmHg · 7 readings
          </div>
        </div>
        <svg viewBox="0 0 240 72" width="100%" height="72" fill="none">
          {/* Reference band 110-120 */}
          <rect x="0" y="24" width="240" height="24" fill={PALETTE.peach} opacity="0.14" />
          <polyline
            points={points
              .map((v, i) => {
                const x = (i / (points.length - 1)) * 236 + 2;
                const y = 68 - ((v - (min - 4)) / (max - (min - 4) + 4)) * 60;
                return `${x},${y}`;
              })
              .join(" ")}
            stroke={PALETTE.peach}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {points.map((v, i) => {
            const x = (i / (points.length - 1)) * 236 + 2;
            const y = 68 - ((v - (min - 4)) / (max - (min - 4) + 4)) * 60;
            return <circle key={i} cx={x} cy={y} r={2.4} fill={PALETTE.peach} />;
          })}
        </svg>
      </div>

      {/* Evidence chip */}
      <div className="mt-4 flex flex-wrap gap-2">
        <div className="text-[11px] px-2.5 py-1 rounded-full bg-neutral-100 dark:bg-white/[0.05] border border-neutral-200 dark:border-white/10 text-neutral-700 dark:text-white/70">
          Based on · Home cuff, Mar–Aug 2026
        </div>
      </div>
    </div>
  );
}

// ---- Theme toggle button ------------------------------------------------
function ThemeToggle({ mode, toggle }) {
  const isDark = mode === "dark";
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="w-10 h-10 rounded-full flex items-center justify-center border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/[0.04] text-neutral-700 dark:text-white/80 hover:bg-neutral-100 dark:hover:bg-white/[0.08] transition-colors"
    >
      {isDark ? <Sun size={16} strokeWidth={1.8} /> : <Moon size={16} strokeWidth={1.8} />}
    </button>
  );
}

function scrollToApply() {
  document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

// ---- How it works: interactive stepper with per-step visual ------------
function StepVisual({ step }) {
  if (step === 0) {
    // Bring it in — sources flowing into a common tray
    const chips = [
      { Icon: FileText,     label: "PDF",    c: PALETTE.blue },
      { Icon: ImageIcon,    label: "Scan",   c: PALETTE.peach },
      { Icon: MessageCircle,label: "Portal", c: PALETTE.lavender },
      { Icon: Watch,        label: "Watch",  c: PALETTE.metabolism },
    ];
    return (
      <div className="relative h-full min-h-[280px] flex flex-col items-center justify-center gap-8">
        <div className="flex flex-wrap gap-3 justify-center">
          {chips.map((c) => (
            <div key={c.label} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-white/[0.05] border border-neutral-200 dark:border-white/10 shadow-sm">
              <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${c.c}22` }}>
                <c.Icon size={14} color={c.c} strokeWidth={1.8} />
              </div>
              <span className="text-[12px] font-medium tracking-tight text-neutral-700 dark:text-white/80">{c.label}</span>
            </div>
          ))}
        </div>
        <svg width="140" height="60" viewBox="0 0 140 60" fill="none" className="text-neutral-300 dark:text-white/20">
          <path d="M20 0 L70 55 L120 0" stroke="currentColor" strokeWidth="1.2" strokeDasharray="4 4" strokeLinecap="round" fill="none" />
        </svg>
        <div className="w-[220px] rounded-2xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${PALETTE.peach}22` }}>
            <Upload size={16} color={PALETTE.peach} strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-[13px] font-medium">One quiet place</div>
            <div className="text-[11px] font-mono text-neutral-500 dark:text-white/45">inbox · auto-grouped</div>
          </div>
        </div>
      </div>
    );
  }
  if (step === 1) {
    // Memora files it — document breakdown into fields
    const rows = [
      { k: "HbA1c",         v: "5.4 %",     c: PALETTE.peach },
      { k: "LDL",           v: "112 mg/dL", c: PALETTE.blue },
      { k: "Blood pressure",v: "118/76",    c: PALETTE.mental },
      { k: "Weight",        v: "72.4 kg",   c: PALETTE.metabolism },
    ];
    return (
      <div className="relative h-full min-h-[280px] flex items-center justify-center gap-8">
        <div className="w-[140px] h-[180px] rounded-2xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-3 shadow-sm flex flex-col justify-between">
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-neutral-500 dark:text-white/50" />
            <span className="text-[11px] font-mono text-neutral-500 dark:text-white/50">lab_2026.pdf</span>
          </div>
          <div className="space-y-1.5">
            {[70, 90, 60, 80, 55, 70].map((w, i) => (
              <div key={i} className="h-1.5 rounded-full bg-neutral-200 dark:bg-white/10" style={{ width: `${w}%` }} />
            ))}
          </div>
          <div className="text-[10px] text-neutral-400 dark:text-white/40 font-mono">page 1/3</div>
        </div>
        <svg width="60" height="20" viewBox="0 0 60 20" className="text-neutral-300 dark:text-white/20">
          <path d="M0 10 L52 10 M46 4 L52 10 L46 16" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div className="w-[260px] rounded-2xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-4 shadow-sm">
          {rows.map((r) => (
            <div key={r.k} className="flex items-center justify-between py-1.5 border-b border-neutral-100 dark:border-white/5 last:border-b-0">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: r.c }} />
                <span className="text-[13px] text-neutral-700 dark:text-white/80">{r.k}</span>
              </div>
              <span className="text-[13px] font-mono text-neutral-900 dark:text-white">{r.v}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  // step 2 — Ask
  return (
    <div className="relative h-full min-h-[280px] flex flex-col items-center justify-center gap-4 max-w-[420px] mx-auto">
      <div className="self-end bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[85%]">
        <p className="text-[13.5px]">Am I trending toward hypertension again?</p>
      </div>
      <div className="self-start w-full rounded-2xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-4">
        <p className="text-[13.5px] leading-[1.55] text-neutral-800 dark:text-white/85">
          You&apos;re back inside the calm range. Systolic averaged{" "}
          <span className="font-mono font-medium">118</span> this month, down from{" "}
          <span className="font-mono font-medium">128</span> in May.
        </p>
        <svg viewBox="0 0 240 48" width="100%" height="48" className="mt-3">
          <rect x="0" y="18" width="240" height="14" fill={PALETTE.peach} opacity="0.14" />
          <polyline
            points="4,10 44,22 84,14 124,18 164,26 204,30 236,36"
            stroke={PALETTE.peach}
            strokeWidth="2.2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div className="mt-3 flex items-center gap-2 text-[11px] font-mono text-neutral-500 dark:text-white/45">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> based on 12 home readings
        </div>
      </div>
    </div>
  );
}

function HowItWorks() {
  const [active, setActive] = useState(0);
  return (
    <section
      data-testid="how-it-works"
      className="relative z-10 max-w-[1180px] mx-auto px-6 md:px-10 py-24 md:py-32 border-t border-neutral-300/60 dark:border-white/10"
    >
      <div className="max-w-3xl mb-14 md:mb-20">
        <div className="text-[11px] tracking-[0.36em] font-medium text-neutral-500 dark:text-white/45 mb-5">
          — HOW IT WORKS
        </div>
        <h2 className="text-[32px] md:text-[52px] font-semibold tracking-[-0.02em] leading-[1.05]">
          Three moves.{" "}
          <span
            className="font-light"
            style={{
              backgroundImage: "linear-gradient(115deg, #E9BFA9, #A9C9E4)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Watch each one.
          </span>
        </h2>
      </div>

      <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-stretch">
        {/* Step selector */}
        <div className="md:col-span-5 space-y-3">
          {STORY.map((s, i) => {
            const on = active === i;
            return (
              <button
                key={s.n}
                onClick={() => setActive(i)}
                data-testid={`step-${i}`}
                className={
                  "w-full text-left rounded-2xl border transition-all p-5 md:p-6 flex items-start gap-4 " +
                  (on
                    ? "border-neutral-900 dark:border-white bg-white dark:bg-white/[0.06] shadow-sm"
                    : "border-neutral-200 dark:border-white/10 bg-white/60 dark:bg-white/[0.02] hover:border-neutral-300 dark:hover:border-white/20")
                }
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: on ? `${s.color}33` : `${s.color}18` }}
                >
                  <s.Icon size={18} color={s.color} strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[11px] font-mono text-neutral-400 dark:text-white/40 tracking-tight">
                      {s.n}
                    </span>
                    <h3 className={"text-[18px] md:text-[20px] font-semibold tracking-tight " + (on ? "" : "text-neutral-700 dark:text-white/80")}>
                      {s.h}
                    </h3>
                  </div>
                  {on && (
                    <p className="mt-2 text-[14px] leading-[1.6] text-neutral-600 dark:text-white/65 font-light">
                      {s.p}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Visual */}
        <div className="md:col-span-7">
          <div
            className="h-full rounded-3xl border border-neutral-200 dark:border-white/10 bg-white/70 dark:bg-white/[0.02] p-6 md:p-10 min-h-[340px] flex items-center justify-center"
            data-testid={`step-visual-${active}`}
          >
            <StepVisual step={active} />
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- Page --------------------------------------------------------------
export default function Landing() {
  const { status } = useAuth();
  const navigate = useNavigate();
  const authed = status === "authed";
  const { mode, toggle } = useThemeMode();

  const [firstName, setFirstName] = useState("");
  const [username, setUsername] = useState("");
  const [surname, setSurname] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  // Ambient orb parallax
  const orbsRef = useRef(null);
  useEffect(() => {
    const el = orbsRef.current;
    if (!el) return;
    const onScroll = () => {
      const y = window.scrollY;
      el.style.transform = `translate3d(0, ${y * -0.08}px, 0)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const fn = firstName.trim();
    const un = username.trim().replace(/^@/, "");
    const sn = surname.trim();
    if (!fn || !un || !sn) {
      setMsg({ text: "First name, surname and username — we need all three.", ok: false });
      return;
    }
    setBusy(true); setMsg(null);
    try {
      await api.post("/waitlist/signup", { first_name: fn, username: un, surname: sn });
      setMsg({ text: "You're on the tester list. We'll be in touch soon.", ok: true });
      setFirstName(""); setUsername(""); setSurname("");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "please try again.";
      setMsg({ text: `Couldn't send that — ${detail}`, ok: false });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#f8f9fa] dark:bg-[#0a0a0f] text-neutral-900 dark:text-white antialiased overflow-x-hidden font-sans transition-colors">
      {/* Ambient gradient orbs */}
      <div
        ref={orbsRef}
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-24 h-[720px] blur-[100px] opacity-[0.75] dark:opacity-[0.35]"
        style={{
          background:
            "radial-gradient(45% 45% at 22% 30%, rgba(246,207,190,0.7) 0%, rgba(246,207,190,0) 70%), radial-gradient(38% 38% at 78% 55%, rgba(185,220,242,0.65) 0%, rgba(185,220,242,0) 70%), radial-gradient(30% 30% at 55% 90%, rgba(217,208,233,0.5) 0%, rgba(217,208,233,0) 70%)",
        }}
      />

      {/* Nav */}
      <header className="relative z-10">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrainMark size={38} />
            <div className="flex items-baseline">
              <span className="text-[22px] font-semibold tracking-tight leading-none">Memora</span>
              <span className="text-[22px] font-semibold leading-none" style={{ color: PALETTE.peach }}>.</span>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <ThemeToggle mode={mode} toggle={toggle} />
            {authed ? (
              <Button
                data-testid="landing-continue"
                onClick={() => navigate("/home")}
                className="bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-white/90 rounded-full h-10 px-5 text-[13px]"
              >
                Continue to Memora <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            ) : (
              <Button
                data-testid="landing-apply"
                onClick={scrollToApply}
                className="bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-white/90 rounded-full h-10 px-5 text-[13px]"
              >
                Become a tester
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-[1180px] mx-auto px-6 md:px-10 pt-10 pb-24 md:pt-20 md:pb-32">
        <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-7 order-2 md:order-1">
            <div className="text-[11px] tracking-[0.36em] font-medium text-neutral-500 dark:text-white/45 mb-8">
              — CLOSED BETA · BY INVITATION
            </div>
            <h1 className="font-semibold tracking-[-0.03em] text-[40px] leading-[1.02] md:text-[76px] md:leading-[1.0]">
              Your health lives in
              <br className="hidden md:block" />
              <span
                className="font-light"
                style={{
                  backgroundImage: "linear-gradient(115deg, #E9BFA9 0%, #D9A9BE 45%, #A9C9E4 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                a hundred filing cabinets.
              </span>
              <br className="hidden md:block" />
              Memora is the one you own.
            </h1>
            <p className="mt-8 text-[17px] md:text-[19px] leading-[1.6] text-neutral-700 dark:text-white/70 max-w-[540px] font-light">
              Every lab, prescription, and clinic visit — gathered from wherever
              it lives, rewritten into words you actually use, and kept safely
              in your hands. A private home for the full picture of your body.
            </p>
            <div className="mt-10">
              {authed ? (
                <Button
                  data-testid="landing-hero-continue"
                  onClick={() => navigate("/home")}
                  className="bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-white/90 rounded-full h-12 px-7 text-[15px]"
                >
                  Continue to Memora <ArrowUpRight className="w-4 h-4 ml-1.5" />
                </Button>
              ) : (
                <Button
                  data-testid="landing-hero-apply"
                  onClick={scrollToApply}
                  className="bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-white/90 rounded-full h-12 px-7 text-[15px]"
                >
                  Apply to test Memora
                </Button>
              )}
            </div>
          </div>
          <div className="md:col-span-5 order-1 md:order-2">
            <HeroConstellation />
          </div>
        </div>
      </section>

      {/* Editorial statement */}
      <section className="relative z-10 max-w-[1180px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="border-t border-neutral-300/60 dark:border-white/10 pt-16 md:pt-24 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.36em] font-medium text-neutral-500 dark:text-white/45">
              — WHY WE BUILT IT
            </div>
          </div>
          <div className="md:col-span-8">
            <p className="text-[22px] md:text-[30px] leading-[1.35] font-light tracking-tight text-neutral-900 dark:text-white/90">
              Your health information is scattered across portals, PDFs, and
              paper — and the person expected to hold it all together is you,
              usually at the worst possible moment. Memora is a home for the
              whole picture.
            </p>
          </div>
        </div>
      </section>

      {/* Product preview — Memora block in action */}
      <section className="relative z-10 max-w-[1180px] mx-auto px-6 md:px-10 py-24 md:py-32 border-t border-neutral-300/60 dark:border-white/10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.36em] font-medium text-neutral-500 dark:text-white/45 mb-5">
              — A LOOK INSIDE
            </div>
            <h2 className="text-[32px] md:text-[48px] font-semibold tracking-[-0.02em] leading-[1.05]">
              Ask a question.
              <br />
              <span
                className="font-light"
                style={{
                  backgroundImage: "linear-gradient(115deg, #E9BFA9, #A9C9E4)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Get your data back.
              </span>
            </h2>
            <p className="mt-6 text-[15px] md:text-[16px] leading-[1.7] text-neutral-600 dark:text-white/70 font-light max-w-[440px]">
              Memora writes the answer in plain words and hands you the
              underlying record — the exact chart, the exact reading, the exact
              visit — so nothing is ever a black box.
            </p>
          </div>
          <div className="md:col-span-7 flex justify-center md:justify-end">
            <ChatPreview />
          </div>
        </div>
      </section>

      {/* Capabilities — five things, presented as a carousel */}
      <section className="relative z-10 max-w-[1180px] mx-auto px-6 md:px-10 py-24 md:py-32 border-t border-neutral-300/60 dark:border-white/10">
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="text-[11px] tracking-[0.36em] font-medium text-neutral-500 dark:text-white/45 mb-5">
            — WHAT IT DOES
          </div>
          <h2 className="text-[32px] md:text-[52px] font-semibold tracking-[-0.02em] leading-[1.05]">
            Five things, done{" "}
            <span
              className="font-light"
              style={{
                backgroundImage: "linear-gradient(115deg, #E9BFA9, #A9C9E4)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              quietly.
            </span>
          </h2>
        </div>

        <Carousel
          opts={{ align: "start", loop: false }}
          className="w-full"
          data-testid="capabilities-carousel"
        >
          <CarouselContent className="-ml-4">
            {CAPABILITIES.map((c, i) => (
              <CarouselItem
                key={c.title}
                className="pl-4 basis-[85%] sm:basis-[55%] md:basis-[42%] lg:basis-[32%]"
                data-testid={`capability-${i}`}
              >
                <div className="h-full rounded-3xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-7 md:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{ backgroundColor: `${c.color}22` }}
                    >
                      <c.Icon size={22} color={c.color} strokeWidth={1.8} />
                    </div>
                    <div className="text-[12px] font-mono tracking-tight text-neutral-400 dark:text-white/35">
                      {String(i + 1).padStart(2, "0")} / {String(CAPABILITIES.length).padStart(2, "0")}
                    </div>
                  </div>
                  <h3 className="text-[24px] md:text-[26px] font-semibold tracking-tight">
                    {c.title}
                  </h3>
                  <p className="mt-4 text-[15px] leading-[1.7] text-neutral-600 dark:text-white/65 font-light">
                    {c.body}
                  </p>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="mt-8 flex items-center justify-end gap-3">
            <CarouselPrevious
              data-testid="capabilities-prev"
              className="static translate-y-0 h-11 w-11 rounded-full border-neutral-200 dark:border-white/10 bg-white dark:bg-white/[0.04] text-neutral-800 dark:text-white hover:bg-neutral-100 dark:hover:bg-white/[0.08]"
            />
            <CarouselNext
              data-testid="capabilities-next"
              className="static translate-y-0 h-11 w-11 rounded-full border-neutral-200 dark:border-white/10 bg-white dark:bg-white/[0.04] text-neutral-800 dark:text-white hover:bg-neutral-100 dark:hover:bg-white/[0.08]"
            />
          </div>
        </Carousel>
      </section>

      {/* How it works — interactive stepper */}
      <HowItWorks />

      {/* FAQ */}
      <section className="relative z-10 max-w-[1180px] mx-auto px-6 md:px-10 py-24 md:py-32 border-t border-neutral-300/60 dark:border-white/10">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.36em] font-medium text-neutral-500 dark:text-white/45 mb-5">
              — FREQUENTLY ASKED
            </div>
            <h2 className="text-[32px] md:text-[52px] font-semibold tracking-[-0.02em] leading-[1.05]">
              Questions we&apos;ve been asked,{" "}
              <span
                className="font-light"
                style={{
                  backgroundImage: "linear-gradient(115deg, #E9BFA9, #A9C9E4)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                answered honestly.
              </span>
            </h2>
          </div>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible defaultValue="faq-0" data-testid="faq" className="w-full">
              {FAQ.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-neutral-300/70 dark:border-white/10">
                  <AccordionTrigger
                    data-testid={`faq-q-${i}`}
                    className="py-6 text-left text-[18px] md:text-[22px] font-medium tracking-tight hover:no-underline"
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-a-${i}`}
                    className="pb-6 text-[15px] md:text-[16px] leading-[1.7] text-neutral-600 dark:text-white/70 font-light max-w-[600px]"
                  >
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Tester application */}
      <section
        id="apply"
        className="relative z-10 max-w-[1180px] mx-auto px-6 md:px-10 py-24 md:py-32 border-t border-neutral-300/60 dark:border-white/10"
      >
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.36em] font-medium text-neutral-500 dark:text-white/45 mb-5">
              — BECOME A TESTER
            </div>
            <h2 className="text-[32px] md:text-[48px] font-semibold tracking-[-0.02em] leading-[1.05]">
              Try Memora{" "}
              <span
                className="font-light"
                style={{
                  backgroundImage: "linear-gradient(115deg, #E9BFA9, #A9C9E4)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                before everyone else.
              </span>
            </h2>
            <p className="mt-6 text-[15px] md:text-[16px] leading-[1.7] text-neutral-600 dark:text-white/70 font-light max-w-[440px]">
              We&apos;re inviting a small group of testers to shape how Memora
              feels before we open the doors. Tell us who you are — we&apos;ll
              reach out through your username.
            </p>
            <div className="mt-8 flex items-center gap-3 text-[13px] text-neutral-500 dark:text-white/55">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span>Currently accepting applications</span>
            </div>
          </div>

          <div className="md:col-span-7">
            <div
              className="rounded-[28px] p-[1.5px]"
              style={{ background: "linear-gradient(135deg, rgba(246,207,190,0.9), rgba(217,169,190,0.6) 45%, rgba(185,220,242,0.9))" }}
            >
              <form
                onSubmit={submit}
                className="rounded-[calc(28px-1.5px)] bg-white/95 dark:bg-[#0a0a0f]/85 backdrop-blur px-7 py-8 md:px-10 md:py-10"
              >
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 dark:text-white/55 mb-2 block font-medium">
                      First name
                    </label>
                    <Input
                      data-testid="tester-first-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Ada"
                      disabled={busy}
                      className="h-12 rounded-xl border-neutral-200 dark:border-white/10 bg-neutral-50/60 dark:bg-white/[0.04] dark:text-white text-[15px] font-sans focus-visible:ring-neutral-300 dark:focus-visible:ring-white/20"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 dark:text-white/55 mb-2 block font-medium">
                      Surname
                    </label>
                    <Input
                      data-testid="tester-surname"
                      value={surname}
                      onChange={(e) => setSurname(e.target.value)}
                      placeholder="Lovelace"
                      disabled={busy}
                      className="h-12 rounded-xl border-neutral-200 dark:border-white/10 bg-neutral-50/60 dark:bg-white/[0.04] dark:text-white text-[15px] font-sans focus-visible:ring-neutral-300 dark:focus-visible:ring-white/20"
                    />
                  </div>
                </div>
                <div className="mt-5">
                  <label className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 dark:text-white/55 mb-2 block font-medium">
                    Username
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-white/40 select-none font-mono">@</span>
                    <Input
                      data-testid="tester-username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value.replace(/^@/, "").replace(/\s+/g, ""))}
                      placeholder="adalovelace"
                      disabled={busy}
                      autoCapitalize="none"
                      autoCorrect="off"
                      className="h-12 rounded-xl border-neutral-200 dark:border-white/10 bg-neutral-50/60 dark:bg-white/[0.04] dark:text-white text-[15px] font-sans pl-8 focus-visible:ring-neutral-300 dark:focus-visible:ring-white/20"
                    />
                  </div>
                  <p className="mt-2 text-[12px] text-neutral-500 dark:text-white/55">
                    We&apos;ll invite you through this handle when your seat opens.
                  </p>
                </div>

                <Button
                  data-testid="tester-submit"
                  type="submit"
                  disabled={busy}
                  className="mt-8 w-full h-12 rounded-full text-[15px] bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-white/90"
                >
                  {busy ? "Sending…" : "Apply to test"}
                </Button>

                {msg && (
                  <div
                    data-testid="tester-msg"
                    className={`mt-5 text-[14px] flex items-start gap-2 ${msg.ok ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}
                  >
                    {msg.ok && <Check className="w-4 h-4 mt-0.5 shrink-0" />}
                    <span>{msg.text}</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-neutral-300/60 dark:border-white/10">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 py-12 grid gap-8 md:grid-cols-3 md:items-start">
          {/* Brand + tagline */}
          <div className="space-y-3">
            <div className="flex items-center gap-2.5">
              <BrainMark size={30} />
              <span className="text-[15px] font-semibold tracking-tight">Memora Health</span>
            </div>
            <p className="text-[13px] text-neutral-600 dark:text-white/60 font-light max-w-[260px]">
              A private, patient-owned home for the full picture of your body.
            </p>
          </div>

          {/* Socials */}
          <div className="space-y-3">
            <div className="text-[11px] uppercase tracking-[0.24em] font-medium text-neutral-500 dark:text-white/45">
              Follow along
            </div>
            <div className="flex items-center gap-2">
              {[
                { Icon: Twitter,   href: "https://twitter.com/memorahealth",   label: "Twitter",   id: "twitter" },
                { Icon: Instagram, href: "https://instagram.com/memorahealth", label: "Instagram", id: "instagram" },
                { Icon: Linkedin,  href: "https://linkedin.com/company/memorahealth", label: "LinkedIn",  id: "linkedin" },
              ].map((s) => (
                <a
                  key={s.id}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  data-testid={`social-${s.id}`}
                  className="w-10 h-10 rounded-full flex items-center justify-center border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/[0.04] text-neutral-700 dark:text-white/80 hover:bg-neutral-100 dark:hover:bg-white/[0.08] transition-colors"
                >
                  <s.Icon size={16} strokeWidth={1.8} />
                </a>
              ))}
            </div>
            <a
              href="mailto:info@memorahealth.app"
              data-testid="footer-contact"
              className="inline-block text-[13px] text-neutral-700 dark:text-white/75 hover:text-neutral-900 dark:hover:text-white transition-colors"
            >
              info@memorahealth.app
            </a>
          </div>

          {/* Access / admin */}
          <div className="space-y-3 md:text-right">
            <div className="text-[11px] uppercase tracking-[0.24em] font-medium text-neutral-500 dark:text-white/45">
              Team
            </div>
            <div className="flex md:justify-end items-center gap-4">
              <a
                href="/api/admin"
                data-testid="footer-admin"
                className="text-[13px] font-medium text-neutral-800 dark:text-white/85 hover:text-neutral-900 dark:hover:text-white underline underline-offset-4 decoration-neutral-300 dark:decoration-white/20"
              >
                Admin
              </a>
              <ThemeToggle mode={mode} toggle={toggle} />
            </div>
          </div>
        </div>

        <div className="border-t border-neutral-300/60 dark:border-white/10">
          <div className="max-w-[1180px] mx-auto px-6 md:px-10 py-6 flex flex-wrap items-center justify-between gap-3">
            <div className="text-[12px] text-neutral-500 dark:text-white/50 font-mono tracking-tight">
              © {new Date().getFullYear()} Memora Health · your data, quietly remembered
            </div>
            <div className="text-[12px] text-neutral-500 dark:text-white/50 font-mono tracking-tight">
              built with care.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
